import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI, squareAPI } from '../services/api';
import { getSquareCache, setSquareCache, sortCategoriesWithCustomOrder } from '../utils/squareCache';

/**
 * 2-step wizard for creating Square service tickets (work orders).
 *
 *   Step 1  - Select products / services from the Square catalog
 *   Step 2  - Select / enter bike & frame info, ticket name, review & save
 *
 * The ticket is saved as an in_progress Service in Framebook and optionally
 * as an OPEN order in Square. Payment happens separately (via SquarePaymentModal)
 * once the work is complete.
 */
function SquareTicketModal({ isOpen, onClose, onSaved, darkMode = true, serviceContext = null }) {
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Catalog / product state ──────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [inventoryByVariation, setInventoryByVariation] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [orderCurrency, setOrderCurrency] = useState('GBP');

  // ── Bike state ───────────────────────────────────────────────
  const [frameNumber, setFrameNumber] = useState('');
  const [ticketName, setTicketName] = useState('');
  const [addNewBike, setAddNewBike] = useState(false);
  const [loadingBikes, setLoadingBikes] = useState(false);
  const [bikes, setBikes] = useState([]);
  const [bikeSearchTerm, setBikeSearchTerm] = useState('');
  const [selectedBikeId, setSelectedBikeId] = useState('');
  const [bikeData, setBikeData] = useState({ brand: '', model: '', color: '', notes: '' });

  // ── Derived data ─────────────────────────────────────────────
  const selectedItems = useMemo(() => {
    return Object.entries(selectedQuantities)
      .filter(([, quantity]) => Number(quantity) > 0)
      .map(([variationId, quantity]) => ({ variation_id: variationId, quantity: Number(quantity) }));
  }, [selectedQuantities]);

  const localSubtotal = useMemo(() => {
    return selectedItems.reduce((sum, selected) => {
      const item = catalogItems.find(c => c.variation_id === selected.variation_id);
      if (!item) return sum;
      return sum + (item.price_cents || 0) * selected.quantity;
    }, 0);
  }, [selectedItems, catalogItems]);

  const selectedBike = useMemo(() => {
    if (!selectedBikeId) return null;
    return bikes.find(bike => String(bike.id) === String(selectedBikeId)) || null;
  }, [bikes, selectedBikeId]);

  const availableCategories = useMemo(() => {
    const cats = new Set();
    catalogItems.forEach(item => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort();
  }, [catalogItems]);

  const filteredCatalogItems = useMemo(() => {
    let items = catalogItems;
    const needle = productSearchTerm.trim().toLowerCase();
    if (needle) {
      items = items.filter(item => {
        const haystack = [item.name, item.item_name, item.variation_name, item.sku, item.category]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(needle);
      });
    }
    if (selectedCategory) items = items.filter(item => item.category === selectedCategory);
    return items;
  }, [catalogItems, productSearchTerm, selectedCategory]);

  const groupedCatalogItems = useMemo(() => {
    const groups = {};
    filteredCatalogItems.forEach(item => {
      const cat = item.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    const keys = sortCategoriesWithCustomOrder(Object.keys(groups));
    return keys.map(key => ({ category: key, items: groups[key] }));
  }, [filteredCatalogItems]);

  const filteredBikes = useMemo(() => {
    const needle = bikeSearchTerm.trim().toLowerCase();
    if (!needle) return bikes.slice(0, 80);
    return bikes
      .filter(bike => {
        const haystack = [
          bike.vin, bike.brand, bike.model,
          bike.owner_name, bike.owner_firstname, bike.owner_surname,
          bike.owner_email, bike.description,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(needle);
      })
      .slice(0, 80);
  }, [bikes, bikeSearchTerm]);

  const canMoveToStep2 = selectedItems.length > 0 && !!locationId;
  const canSaveTicket = frameNumber.trim().length > 0 && (!addNewBike || (bikeData.brand && bikeData.model));

  // ── Helpers ──────────────────────────────────────────────────
  const changeQuantity = (variationId, delta) => {
    setSelectedQuantities(prev => {
      const current = Number(prev[variationId] || 0);
      const next = Math.max(0, current + delta);
      return { ...prev, [variationId]: next };
    });
  };

  const resetState = () => {
    setStep(1);
    setSavingTicket(false);
    setError('');
    setSuccess('');
    setLocations([]);
    setLocationId(null);
    setCatalogItems([]);
    setInventoryByVariation({});
    setSelectedQuantities({});
    setProductSearchTerm('');
    setSelectedCategory('');
    setFrameNumber('');
    setTicketName('');
    setAddNewBike(false);
    setLoadingBikes(false);
    setBikes([]);
    setBikeSearchTerm('');
    setSelectedBikeId('');
    setBikeData({ brand: '', model: '', color: '', notes: '' });
    setOrderCurrency('GBP');
  };

  // ── Data loaders ─────────────────────────────────────────────
  const loadLocations = async (fallbackLocationId) => {
    try {
      setLoadingLocations(true);
      const response = await squareAPI.listLocations();
      const locationOptions = response.data.locations || [];
      setLocations(locationOptions);
      const resolved = fallbackLocationId || response.data.default_location_id || locationOptions[0]?.id || null;
      setLocationId(resolved);
      return resolved;
    } catch {
      setLocations([]);
      const resolved = fallbackLocationId || null;
      setLocationId(resolved);
      return resolved;
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadInventoryForLocation = async (resolvedLocationId, items) => {
    if (!resolvedLocationId || !items || items.length === 0) { setInventoryByVariation({}); return; }
    const variationIds = items.map(i => i.variation_id).filter(Boolean);
    if (variationIds.length === 0) { setInventoryByVariation({}); return; }
    const inventoryRes = await squareAPI.getInventory(resolvedLocationId, variationIds, false);
    const baseCounts = {};
    variationIds.forEach(id => { baseCounts[id] = 0; });
    Object.entries(inventoryRes.data?.counts || {}).forEach(([variationId, quantity]) => {
      const parsed = Number(quantity);
      baseCounts[variationId] = Number.isFinite(parsed) ? parsed : 0;
    });
    setInventoryByVariation(baseCounts);
  };

  const loadBikes = async () => {
    try {
      setLoadingBikes(true);
      const response = await adminAPI.getAllBikes();
      setBikes(response.data?.bikes || []);
    } catch (err) {
      setBikes([]);
      setError(err.response?.data?.error || 'Failed to load bike list');
    } finally {
      setLoadingBikes(false);
    }
  };

  const loadCatalogAndInventory = async (resolvedLocationId) => {
    try {
      const bundleRes = await squareAPI.getCatalogBundle(resolvedLocationId, false);
      const catalog = bundleRes.data?.catalog || {};
      const items = catalog.items || [];
      const invCounts = bundleRes.data?.inventory_counts || {};
      setCatalogItems(items);
      if (items.length > 0) setOrderCurrency(items[0].currency || 'GBP');
      if (Object.keys(invCounts).length > 0) {
        const baseCounts = {};
        items.forEach(i => { baseCounts[i.variation_id] = 0; });
        Object.entries(invCounts).forEach(([vid, qty]) => {
          baseCounts[vid] = Number.isFinite(Number(qty)) ? Number(qty) : 0;
        });
        setInventoryByVariation(baseCounts);
      }
      setSquareCache({
        catalog_items: items, inventory_counts: invCounts,
        location_id: resolvedLocationId, connected: true,
        last_synced_at: new Date().toISOString(),
      });
    } catch {
      try {
        const catalogRes = await squareAPI.getCatalog(false);
        const items = catalogRes.data.items || [];
        setCatalogItems(items);
        if (items.length > 0) setOrderCurrency(items[0].currency || 'GBP');
        try { await loadInventoryForLocation(resolvedLocationId, items); } catch { setInventoryByVariation({}); }
      } catch { /* ignore */ }
    }
  };

  const initialize = async () => {
    const cached = getSquareCache();
    if (cached?.catalog_items?.length > 0) {
      setCatalogItems(cached.catalog_items);
      setLocationId(cached.location_id || null);
      setLocations(Array.isArray(cached.locations) ? cached.locations : []);
      if (cached.inventory_counts) setInventoryByVariation(cached.inventory_counts);
      if (cached.catalog_items[0]?.currency) setOrderCurrency(cached.catalog_items[0].currency);
      setLoading(false);
    }
    try {
      if (!cached?.catalog_items?.length) setLoading(true);
      const statusRes = await squareAPI.getStatus();
      if (!statusRes.data?.feature_available) { setError('Square integration requires Enterprise plan.'); setLoading(false); return; }
      const conn = statusRes.data?.connection;
      if (!conn || conn.status !== 'connected') { setError('Square is not connected. Connect it first in Settings > Integrations.'); setLoading(false); return; }
      const resolvedLocationId = await loadLocations(conn.square_location_id || null);
      await Promise.all([loadCatalogAndInventory(resolvedLocationId), loadBikes()]);
      setError('');
    } catch (err) {
      if (!cached?.catalog_items?.length) setError(err.response?.data?.error || 'Failed to initialize Square ticket wizard');
    } finally {
      setLoading(false);
    }
  };

  // ── Effects ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    resetState();
    initialize();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || step !== 1 || !locationId || catalogItems.length === 0) return;
    const refreshInventory = async () => {
      try { await loadInventoryForLocation(locationId, catalogItems); } catch { /* ignore */ }
    };
    refreshInventory();
  }, [isOpen, step, locationId, catalogItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedBike) return;
    setFrameNumber(selectedBike.vin || '');
    setAddNewBike(false);
    if (!ticketName && selectedBike) {
      const autoName = [selectedBike.owner_name, `${selectedBike.brand || ''} ${selectedBike.model || ''}`.trim()]
        .filter(Boolean).join(' ');
      if (autoName) setTicketName(autoName);
    }
  }, [selectedBike]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !serviceContext) return;
    if (serviceContext.bike_id) setSelectedBikeId(String(serviceContext.bike_id));
    if (serviceContext.bike_vin) setFrameNumber(serviceContext.bike_vin);
  }, [isOpen, serviceContext]);

  // ── Save ticket (creates Square order + Framebook service as in_progress) ──
  const handleSaveTicket = async () => {
    if (!frameNumber.trim()) {
      setError('Frame number is required.');
      return;
    }

    try {
      setSavingTicket(true);
      setError('');

      const lineItemsPayload = selectedItems.map(si => ({
        variation_id: si.variation_id,
        quantity: si.quantity,
      }));

      const res = await squareAPI.createOrder({
        location_id: locationId,
        line_items: lineItemsPayload,
        frame_number: frameNumber.trim(),
        ticket_name: ticketName.trim() || undefined,
        bike_id: selectedBikeId || undefined,
        add_new_bike: addNewBike || undefined,
        bike_data: addNewBike ? bikeData : undefined,
        save_as_ticket: true,
      });

      setSuccess('Service ticket created! It will appear as "In Progress" in your services list.');
      if (onSaved) onSaved({
        ticketSaved: true,
        orderId: res.data?.order_id,
        serviceId: res.data?.service_id,
      });
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to create service ticket');
    } finally {
      setSavingTicket(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !savingTicket && onClose()}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', position: 'relative' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Create service ticket</h2>
          <button className="close-btn" onClick={onClose} disabled={savingTicket}>x</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
            Step {step} / 2
          </div>

          {serviceContext?.id && (
            <div style={{
              marginBottom: '12px', padding: '10px 12px', borderRadius: '8px',
              background: darkMode ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)',
              border: darkMode ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(59,130,246,0.2)',
              fontSize: '13px', color: darkMode ? '#dbeafe' : '#1d4ed8',
            }}>
              Linked service #{serviceContext.id}
              {serviceContext.bike_vin ? ` - Frame ${serviceContext.bike_vin}` : ''}
            </div>
          )}

          {loading && <div className="loading">Loading Square data...</div>}
          {error && <div className="error" style={{ marginBottom: '12px' }}>{error}</div>}
          {success && <div className="success" style={{ marginBottom: '12px' }}>{success}</div>}

          {/* ──────────────────── STEP 1: SELECT PRODUCTS ──────────────────── */}
          {!loading && step === 1 && (
            <div>
              <h3 style={{ marginTop: 0 }}>Select services &amp; products</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Location</label>
                  <select
                    value={locationId || ''}
                    onChange={(e) => setLocationId(e.target.value || null)}
                    disabled={loadingLocations || locations.length === 0}
                  >
                    <option value="">Default location</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} {loc.is_default ? '(default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {!locationId && (
                <div style={{ marginBottom: '12px', fontSize: '13px', color: '#f59e0b' }}>
                  No Square location selected. Choose a location to continue.
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  style={{
                    flex: '1 1 200px', padding: '10px 14px', borderRadius: '10px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.14)',
                    background: darkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#111827', fontSize: '14px',
                  }}
                />
                {availableCategories.length > 0 && (
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{
                      flex: '0 1 220px', padding: '10px 14px', borderRadius: '10px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.14)',
                      background: darkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#111827', fontSize: '14px',
                    }}
                  >
                    <option value="">All categories</option>
                    {availableCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.1)', borderRadius: '10px' }}>
                {filteredCatalogItems.length === 0 ? (
                  <div style={{ padding: '16px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
                    {catalogItems.length === 0 ? 'No sellable catalog variations found.' : 'No products match your search/filter.'}
                  </div>
                ) : (
                  groupedCatalogItems.map(group => (
                    <div key={group.category}>
                      <div style={{
                        padding: '8px 14px',
                        background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                        fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                        color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280',
                        position: 'sticky', top: 0, zIndex: 1,
                      }}>
                        {group.category} ({group.items.length})
                      </div>
                      {group.items.map(item => {
                        const stockRaw = Number(inventoryByVariation[item.variation_id]);
                        const stock = Number.isFinite(stockRaw) ? Math.max(0, stockRaw) : 0;
                        const selectedQty = Number(selectedQuantities[item.variation_id] || 0);
                        return (
                          <div key={item.variation_id} style={{
                            display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center',
                            padding: '10px 14px',
                            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                            background: selectedQty > 0 ? (darkMode ? 'rgba(74, 140, 24, 0.1)' : 'rgba(74, 140, 24, 0.06)') : 'transparent',
                          }}>
                            <div>
                              <div style={{ color: darkMode ? '#ffffff' : '#111827', fontWeight: 600, fontSize: '14px' }}>{item.name}</div>
                              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>
                                {item.sku ? `SKU: ${item.sku} · ` : ''}Stock: {stock}
                              </div>
                            </div>
                            <div style={{ color: darkMode ? '#ffffff' : '#111827', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>
                              {(item.price || 0).toFixed(2)} {item.currency || 'GBP'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button type="button" className="btn btn-secondary" style={{ minWidth: '32px', padding: '4px 8px' }}
                                onClick={() => changeQuantity(item.variation_id, -1)} disabled={selectedQty <= 0}>-</button>
                              <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 600 }}>{selectedQty}</span>
                              <button type="button" className="btn btn-secondary" style={{ minWidth: '32px', padding: '4px 8px' }}
                                onClick={() => changeQuantity(item.variation_id, 1)}>+</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>

              <div style={{ marginTop: '16px', fontWeight: 600, color: darkMode ? '#ffffff' : '#111827' }}>
                Estimated total: {(localSubtotal / 100).toFixed(2)} {catalogItems[0]?.currency || 'GBP'}
              </div>
            </div>
          )}

          {/* ──────────────────── STEP 2: BIKE / FRAME + TICKET NAME + SUMMARY ──────────────────── */}
          {!loading && step === 2 && (
            <div>
              <h3 style={{ marginTop: 0 }}>Bike &amp; ticket details</h3>

              {/* Ticket name */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Ticket name</label>
                <input
                  type="text"
                  value={ticketName}
                  onChange={(e) => setTicketName(e.target.value)}
                  placeholder="e.g. John Smith Trek Domane"
                  style={{ width: '100%' }}
                />
                <div style={{ marginTop: '4px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>
                  Visible in Square POS and Framebook. Typically: customer name + bike model.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Select existing bike (optional)</label>
                <input
                  type="text" value={bikeSearchTerm}
                  onChange={(e) => setBikeSearchTerm(e.target.value)}
                  placeholder="Search by frame number, brand, model, customer..."
                  style={{ marginBottom: '8px' }}
                />
                <div style={{
                  maxHeight: '180px', overflowY: 'auto',
                  border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '10px',
                }}>
                  {loadingBikes && (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#4b5563' }}>Loading bikes...</div>
                  )}
                  {!loadingBikes && filteredBikes.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.65)' : '#4b5563' }}>No bikes found for this search.</div>
                  )}
                  {!loadingBikes && filteredBikes.map(bike => {
                    const isSelected = String(selectedBikeId) === String(bike.id);
                    return (
                      <button key={bike.id} type="button"
                        onClick={() => { setSelectedBikeId(String(bike.id)); setAddNewBike(false); }}
                        style={{
                          width: '100%', textAlign: 'left', border: 'none',
                          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                          background: isSelected ? (darkMode ? 'rgba(74, 140, 24, 0.24)' : 'rgba(26, 40, 17, 0.12)') : 'transparent',
                          color: darkMode ? '#ffffff' : '#111827', padding: '10px 12px', cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{bike.brand} {bike.model} ({bike.vin})</div>
                        <div style={{ fontSize: '12px', opacity: 0.75 }}>{bike.owner_name || bike.owner_email || 'No linked customer'}</div>
                      </button>
                    );
                  })}
                </div>
                {selectedBike && (
                  <div style={{
                    marginTop: '8px', padding: '8px 10px', borderRadius: '8px',
                    background: darkMode ? 'rgba(74, 140, 24, 0.2)' : 'rgba(26, 40, 17, 0.1)',
                    border: darkMode ? '1px solid rgba(74, 140, 24, 0.35)' : '1px solid rgba(26, 40, 17, 0.25)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                  }}>
                    <span style={{ fontSize: '13px' }}>Selected: {selectedBike.brand} {selectedBike.model} ({selectedBike.vin})</span>
                    <button type="button" className="btn btn-secondary" onClick={() => { setSelectedBikeId(''); setFrameNumber(''); }}>Clear</button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Frame number *</label>
                <input type="text" value={frameNumber} onChange={(e) => setFrameNumber(e.target.value)}
                  placeholder="Bike frame number / VIN" readOnly={Boolean(selectedBikeId)} required />
                {selectedBikeId && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280' }}>
                    Frame number is locked to the selected bike. Clear selection to edit manually.
                  </div>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <input type="checkbox" checked={addNewBike}
                  onChange={(e) => setAddNewBike(e.target.checked)} disabled={Boolean(selectedBikeId)} />
                Add new bike?
              </label>

              {addNewBike && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                  <div className="form-group"><label>Brand *</label><input value={bikeData.brand} onChange={(e) => setBikeData({ ...bikeData, brand: e.target.value })} /></div>
                  <div className="form-group"><label>Model *</label><input value={bikeData.model} onChange={(e) => setBikeData({ ...bikeData, model: e.target.value })} /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <textarea rows={2} value={bikeData.notes} onChange={(e) => setBikeData({ ...bikeData, notes: e.target.value })} />
                  </div>
                </div>
              )}

              {/* ── Ticket summary ── */}
              <div style={{
                marginTop: '16px', padding: '14px', borderRadius: '10px',
                background: darkMode ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)',
                border: `1px solid ${darkMode ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.2)'}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: darkMode ? '#60a5fa' : '#2563eb', marginBottom: '8px' }}>
                  Ticket summary
                </div>
                <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
                  {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} &middot; Total: {(localSubtotal / 100).toFixed(2)} {orderCurrency}
                </div>
                {ticketName && (
                  <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', marginTop: '4px' }}>
                    Ticket: {ticketName}
                  </div>
                )}
                <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', marginTop: '4px' }}>
                  Frame: {frameNumber || '(not set)'}
                  {selectedBike && <span> &middot; {selectedBike.brand} {selectedBike.model}</span>}
                </div>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af', marginTop: '8px' }}>
                  This will create an in-progress service in Framebook. Payment can be processed later when the work is complete.
                </div>
              </div>
            </div>
          )}

          {/* ──────────────────── NAV BUTTONS ──────────────────── */}
          <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {step === 2 && (
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} disabled={savingTicket}>
                Back
              </button>
            )}
            {step === 1 && (
              <button type="button" className="btn btn-primary" onClick={() => setStep(2)} disabled={!canMoveToStep2}>
                Next: Bike &amp; details
              </button>
            )}
            {step === 2 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveTicket}
                disabled={savingTicket || !canSaveTicket}
                style={{
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#fff', border: 'none', fontWeight: 600, fontSize: '15px',
                  padding: '10px 24px',
                }}
              >
                {savingTicket ? 'Creating ticket...' : 'Save service ticket'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={savingTicket}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SquareTicketModal;
